import { Switch } from "../../primitives/Switch";
import { Control, Controller } from "react-hook-form";
import { FieldPath, FieldPathValue, FieldValues } from "react-hook-form";
import { cn } from "@gettactic/helpers";
import { ComponentPropsWithoutRef } from "react";

export declare type ToggleInputItemProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
  control?: Control<TFieldValues>;
  name: TName;
  title: string;
  description: string;
  defaultValue?: FieldPathValue<TFieldValues, TName>;
  switchProps?: Omit<ComponentPropsWithoutRef<typeof Switch>, "checked"> & {
    onCheckedChange?: (checked: boolean) => void;
  };
  descriptionClassName?: string;
};

export const ToggleInputItem = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: ToggleInputItemProps<TFieldValues, TName>
) => {
  const { control, defaultValue, name, title, description, switchProps } =
    props;
  const { onCheckedChange: customOnCheckedChange, ...restSwitchProps } =
    switchProps ?? {};

  return (
    <div className="col-span-12 grid grid-cols-12 items-center justify-between gap-x-8">
      <div className="col-span-10 flex flex-col">
        <p className="font-medium text-neutral-900">{title}</p>
        <p
          className={cn(
            "pr-10 text-justify text-neutral-700",
            props.descriptionClassName
          )}
        >
          {description}
        </p>
      </div>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) => (
          <Switch
            checked={!!value}
            onCheckedChange={(checked) => {
              onChange(checked);
              customOnCheckedChange?.(checked);
            }}
            className="col-span-2 ml-4 justify-self-end"
            {...restSwitchProps}
          />
        )}
      />
    </div>
  );
};
